import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { ALLOWED_DOCUMENT_MIME_TYPES, MAX_DOCUMENT_SIZE_BYTES } from "@shared/finance/supplierInvoices";
import { formatInstallationReference, installationMatchesSearchQuery } from "@shared/finance/manualExpenses";
import { ChevronsUpDown, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { SupplierCreateModal } from "./SupplierCreateModal";
import { selectSupplierInDraft } from "@shared/finance/suppliers";
import { uploadSupplierInvoiceDocumentFile } from "./SupplierInvoiceDocuments";
import { prepareSupplierInvoiceDuplicate, type SupplierInvoiceDuplicateSeed } from "@/finance/supplierInvoiceDuplicate";

const VAT_RATES = [
  { label: "0%", value: "0" },
  { label: "4%", value: "4" },
  { label: "10%", value: "10" },
  { label: "21%", value: "21" },
] as const;

const NO_CATEGORY_VALUE = "__automatic__";

function moneyCentsForForm(value: unknown): number | null {
  if (typeof value !== "string" && typeof value !== "number") return null;
  const text = String(value).trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(text)) return null;
  const [whole, fraction = ""] = text.split(".");
  return Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
}

function asNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const numeric = Number(value);
  return Number.isSafeInteger(numeric) && numeric > 0 ? numeric : null;
}

function normalizePersistedVatRate(value: unknown): string {
  if (typeof value !== "string" && typeof value !== "number") return "";
  const raw = String(value).trim();
  if (!raw || (typeof value === "string" && !/^\d+(?:\.\d+)?$/.test(raw))) return "";
  const numeric = Number(raw);
  if (!Number.isFinite(numeric)) return "";
  return VAT_RATES.find((rate) => Number(rate.value) === numeric)?.value ?? "";
}

const ALLOCATION_OPTIONS = [
  { value: "installation", label: "Obra / expediente" },
  { value: "warehouse", label: "Almacén" },
] as const;

interface SupplierInvoiceFormProps {
  invoiceId?: number;
  duplicateFromInvoiceId?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

type FormState = {
  supplierId: string;
  invoiceNumber: string;
  invoiceDate: string;
  expectedPaymentDate: string;
  concept: string;
  allocationTarget: "installation" | "warehouse";
  installationId: string;
  taxableBase: string;
  vatRate: string;
  categoryCode: string;
  notes: string;
  overrideReason: string;
};

const EMPTY_FORM: FormState = {
  supplierId: "",
  invoiceNumber: "",
  invoiceDate: "",
  expectedPaymentDate: "",
  concept: "",
  allocationTarget: "installation",
  installationId: "",
  taxableBase: "",
  vatRate: "21",
  categoryCode: NO_CATEGORY_VALUE,
  notes: "",
  overrideReason: "",
};

export function SupplierInvoiceForm({ invoiceId, duplicateFromInvoiceId, open, onOpenChange, onSuccess }: SupplierInvoiceFormProps) {
  const utils = trpc.useUtils();
  const suppliersQuery = trpc.productCatalog.listSuppliers.useQuery({ active: true }, { enabled: open });
  const installationsQuery = trpc.installations.list.useQuery(undefined, { enabled: open });
  const categoriesQuery = trpc.financialCategories.list.useQuery({ includeInactive: false }, { enabled: open });
  const sourceInvoiceId = duplicateFromInvoiceId ?? invoiceId;
  const editQuery = trpc.supplierInvoices.getById.useQuery({ invoiceId: sourceInvoiceId ?? 0 }, { enabled: open && Boolean(sourceInvoiceId) });
  const editAggregate = editQuery.data as any;
  const duplicateResult = duplicateFromInvoiceId && editAggregate?.invoice
    ? prepareSupplierInvoiceDuplicate(editAggregate as any)
    : null;
  const duplicateSeed: SupplierInvoiceDuplicateSeed | null = duplicateResult?.ok ? duplicateResult.seed : null;
  const editLineCount = Array.isArray(editAggregate?.lines) ? editAggregate.lines.length : 0;
  const editAllocationCount = Array.isArray(editAggregate?.allocations) ? editAggregate.allocations.length : 0;
  const complexAggregateEditBlocked = Boolean(
    invoiceId && editAggregate && (editLineCount !== 1 || editAllocationCount !== 1),
  );
  const duplicateBlockedReason = duplicateFromInvoiceId
    ? duplicateResult && !duplicateResult.ok
      ? duplicateResult.reason
      : duplicateSeed && duplicateSeed.categoryCode && !categoriesQuery.isLoading && !(categoriesQuery.data ?? []).some((category: any) => category.code === duplicateSeed.categoryCode)
        ? "La categoría de origen no está disponible para crear un nuevo borrador; no se cambiará automáticamente."
        : null
    : null;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [supplierComboOpen, setSupplierComboOpen] = useState(false);
  const [installationComboOpen, setInstallationComboOpen] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(false);
  const [attachment, setAttachment] = useState<File | null>(null);
  const initializedDraft = useRef<string | null>(null);

  const selectedInstallation = (installationsQuery.data ?? []).find((inst: any) => String(inst.id) === form.installationId);

  useEffect(() => {
    if (!open) { initializedDraft.current = null; return; }
    const draftKey = duplicateFromInvoiceId ? `duplicate:${duplicateFromInvoiceId}` : invoiceId ? `edit:${invoiceId}` : "new";
    if (initializedDraft.current === draftKey) return;
    if (!invoiceId && !duplicateFromInvoiceId) {
      setForm(EMPTY_FORM);
      setAttachment(null);
      setDuplicateWarning(false);
      initializedDraft.current = draftKey;
      return;
    }
    const aggregate = editQuery.data as any;
    if (!aggregate?.invoice) return;
    if (Number(aggregate.invoice.id) !== sourceInvoiceId) return;
    initializedDraft.current = draftKey;
    const invoice = aggregate.invoice;
    const line = aggregate.lines?.[0];
    const allocation = aggregate.allocations?.[0];
    if (duplicateFromInvoiceId) {
      const result = prepareSupplierInvoiceDuplicate(aggregate);
      if (!result.ok) { setAttachment(null); setDuplicateWarning(false); return; }
      const seed = result.seed;
      setForm({
        supplierId: String(seed.supplierId), invoiceNumber: "", invoiceDate: "", expectedPaymentDate: "",
        concept: seed.form.concept, allocationTarget: seed.form.allocationTarget, installationId: seed.form.installationId,
        taxableBase: seed.form.taxableBase, vatRate: seed.form.vatRate, categoryCode: seed.form.categoryCode,
        notes: seed.form.notes, overrideReason: "",
      });
    } else {
      setForm({
        supplierId: String(invoice.supplierId ?? ""),
        invoiceNumber: String(invoice.invoiceNumber ?? ""),
        invoiceDate: String(invoice.invoiceDate ?? "").slice(0, 10),
        expectedPaymentDate: String(invoice.expectedPaymentDate ?? "").slice(0, 10),
        concept: String(line?.description ?? ""),
        allocationTarget: allocation?.targetType === "warehouse" ? "warehouse" : "installation",
        installationId: allocation?.targetId ? String(allocation.targetId) : "",
        taxableBase: String(invoice.taxableBase ?? ""),
        vatRate: normalizePersistedVatRate(line?.taxRate),
        categoryCode: String(invoice.categoryCode ?? "__automatic__"),
        notes: String(invoice.notes ?? ""),
        overrideReason: "",
      });
    }
    setAttachment(null);
    setDuplicateWarning(false);
  }, [open, invoiceId, duplicateFromInvoiceId, sourceInvoiceId, editQuery.data]);

  const taxableBaseNum = parseFloat(form.taxableBase);
  const vatRateNum = parseFloat(form.vatRate);
  const vatAmountCalc =
    !Number.isNaN(taxableBaseNum) && !Number.isNaN(vatRateNum) && taxableBaseNum >= 0
      ? Math.round(taxableBaseNum * vatRateNum) / 100
      : null;
  const totalCalc = vatAmountCalc === null ? null : Number((taxableBaseNum + vatAmountCalc).toFixed(2));

  const createMutation = trpc.supplierInvoices.createDraft.useMutation({
    onSuccess: async (invoice) => {
      if (attachment) {
        try {
          await uploadSupplierInvoiceDocumentFile(invoice.id, attachment);
          toast.success("Factura y archivo guardados correctamente");
        } catch {
          toast.warning("La factura se guardó, pero no se pudo adjuntar el archivo.");
        }
      } else {
        toast.success("Factura creada correctamente");
      }
      utils.supplierInvoices.list.invalidate();
      onOpenChange(false);
      onSuccess?.();
      setForm(EMPTY_FORM);
      setAttachment(null);
      setDuplicateWarning(false);
    },
    onError: (err) => {
      if (err.message.includes("SUPPLIER_INVOICE_DUPLICATE_WARNING")) {
        setDuplicateWarning(true);
        toast.error("Posible factura duplicada detectada");
      } else {
        toast.error("Error al crear factura: " + err.message);
      }
    },
  });

  const updateMutation = trpc.supplierInvoices.updateDraft.useMutation({
    onSuccess: async (invoice) => {
      if (attachment) {
        try {
          await uploadSupplierInvoiceDocumentFile(invoice.id, attachment);
          toast.success("Factura y archivo actualizados correctamente");
        } catch {
          toast.warning("La factura se actualizó, pero no se pudo adjuntar el archivo.");
        }
      } else {
        toast.success("Factura actualizada correctamente");
      }
      utils.supplierInvoices.list.invalidate();
      utils.supplierInvoices.getById.invalidate({ invoiceId: invoiceId! });
      onOpenChange(false);
      onSuccess?.();
      setForm(EMPTY_FORM);
      setAttachment(null);
    },
    onError: (err) => toast.error("Error al actualizar factura: " + err.message),
  });

  function set(field: keyof FormState) {
    return (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((previous) => ({ ...previous, [field]: event.target.value }));
    };
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (duplicateFromInvoiceId && duplicateBlockedReason) return void toast.error(duplicateBlockedReason);
    if (duplicateFromInvoiceId && !duplicateSeed) return void toast.error("No se pudo preparar el duplicado de forma segura.");
    if (invoiceId && complexAggregateEditBlocked) {
      return void toast.error("Esta factura contiene varias líneas o imputaciones y debe gestionarse desde el detalle.");
    }
    const supplierId = Number.parseInt(form.supplierId, 10);
    if (!supplierId) return void toast.error("Selecciona un proveedor");
    if (!form.invoiceNumber.trim()) return void toast.error("El número de factura es obligatorio");
    if (!form.invoiceDate) return void toast.error("La fecha de factura es obligatoria");
    if (!form.concept.trim()) return void toast.error("El concepto es obligatorio");
    if (form.allocationTarget === "installation" && !form.installationId) return void toast.error("Selecciona una obra o expediente");
    if (Number.isNaN(taxableBaseNum) || taxableBaseNum < 0 || vatAmountCalc === null) return void toast.error("Completa una base imponible e IVA válidos");
    if (attachment && (!(ALLOWED_DOCUMENT_MIME_TYPES as readonly string[]).includes(attachment.type) || attachment.size > MAX_DOCUMENT_SIZE_BYTES)) {
      return void toast.error("El archivo debe ser PDF, JPG o PNG y no superar 20 MB");
    }

    const selectedCategory = (categoriesQuery.data ?? []).find((category: any) => category.code === form.categoryCode);
    const categoryCode = form.categoryCode === NO_CATEGORY_VALUE ? undefined : form.categoryCode;
    const duplicateAmountsUnchanged = Boolean(duplicateSeed) &&
      moneyCentsForForm(form.taxableBase) === moneyCentsForForm(String(duplicateSeed!.invoice.taxableBase)) &&
      Number(form.vatRate) === Number(duplicateSeed!.line.taxRate);
    const taxableBaseValue = duplicateAmountsUnchanged ? duplicateSeed!.invoice.taxableBase : taxableBaseNum;
    const vatAmountValue = duplicateAmountsUnchanged ? duplicateSeed!.invoice.vatAmount : vatAmountCalc;
    const totalValue = duplicateAmountsUnchanged ? duplicateSeed!.invoice.totalAmount : totalCalc!;
    const duplicateDestinationUnchanged = Boolean(duplicateSeed) &&
      form.allocationTarget === duplicateSeed!.allocation.targetType &&
      (form.allocationTarget === "warehouse" || Number(form.installationId) === Number(duplicateSeed!.allocation.targetId));
    const duplicateAllocationUnchanged = duplicateDestinationUnchanged && duplicateAmountsUnchanged;
    const duplicateCategoryUnchanged = Boolean(duplicateSeed) && form.categoryCode === duplicateSeed!.form.categoryCode;
    const line = duplicateAmountsUnchanged
      ? {
          description: form.concept.trim(),
          productId: duplicateSeed!.line.productId ?? null,
          quantity: duplicateSeed!.line.quantity,
          unit: duplicateSeed!.line.unit,
          unitPrice: duplicateSeed!.line.unitPrice,
          taxableBase: duplicateSeed!.line.taxableBase,
          taxRate: duplicateSeed!.line.taxRate,
          vatAmount: duplicateSeed!.line.vatAmount,
          totalAmount: duplicateSeed!.line.totalAmount,
          position: Number(duplicateSeed!.line.position),
          financialCategoryId: duplicateCategoryUnchanged ? duplicateSeed!.line.financialCategoryId ?? null : selectedCategory?.id ?? null,
        }
      : {
          description: form.concept.trim(), quantity: 1, unit: "ud", unitPrice: taxableBaseNum, taxableBase: taxableBaseNum,
          taxRate: vatRateNum, vatAmount: vatAmountCalc, totalAmount: totalCalc!, position: 1,
          financialCategoryId: selectedCategory?.id ?? undefined,
        };
    const allocation = duplicateAllocationUnchanged
      ? {
          targetType: duplicateSeed!.allocation.targetType,
          targetId: duplicateSeed!.allocation.targetType === "installation" ? Number(duplicateSeed!.allocation.targetId) : null,
          allocatedAmount: duplicateSeed!.allocation.allocatedAmount,
          allocatedPercentage: duplicateSeed!.allocation.allocatedPercentage,
          notes: duplicateSeed!.allocation.notes ?? null,
        }
      : {
          targetType: duplicateDestinationUnchanged ? duplicateSeed!.allocation.targetType : form.allocationTarget,
          targetId: duplicateDestinationUnchanged
            ? duplicateSeed!.allocation.targetType === "installation" ? Number(duplicateSeed!.allocation.targetId) : null
            : form.allocationTarget === "installation" ? Number(form.installationId) : null,
          allocatedAmount: totalCalc!,
          allocatedPercentage: duplicateDestinationUnchanged ? Number(duplicateSeed!.allocation.allocatedPercentage) : 100,
        };
    if (invoiceId) {
      updateMutation.mutate({
        invoiceId,
        supplierId,
        invoiceNumber: form.invoiceNumber.trim(),
        invoiceDate: form.invoiceDate,
        expectedPaymentDate: form.expectedPaymentDate || null,
        taxableBase: taxableBaseNum,
        vatAmount: vatAmountCalc,
        totalAmount: totalCalc!,
        financialCategoryId: selectedCategory?.id ?? null,
        notes: form.notes || null,
        line,
        allocation,
      });
      return;
    }
    createMutation.mutate({
      supplierId,
      invoiceNumber: form.invoiceNumber.trim(),
      invoiceDate: form.invoiceDate,
      expectedPaymentDate: form.expectedPaymentDate || undefined,
      taxableBase: taxableBaseValue,
      vatAmount: vatAmountValue,
      totalAmount: totalValue,
      currency: duplicateSeed?.currency,
      financialCategoryId: duplicateCategoryUnchanged ? asNullableNumber(duplicateSeed!.invoice.financialCategoryId) : selectedCategory?.id ?? null,
      categoryCode,
      overrideReason: duplicateWarning ? form.overrideReason.trim() : undefined,
      notes: form.notes || undefined,
      lines: [line],
      allocations: [allocation],
    });
  }

  function handleSupplierCreated(supplier: { id: number; name: string; taxId?: string | null }) {
    utils.productCatalog.listSuppliers.invalidate();
    setForm((previous) => selectSupplierInDraft(previous, supplier.id));
    setSupplierModalOpen(false);
    setSupplierComboOpen(false);
  }

  const suppliers = suppliersQuery.data ?? [];
  const selectedSupplier = suppliers.find((supplier: any) => String(supplier.id) === form.supplierId);
  const categories = categoriesQuery.data ?? [];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90dvh] overflow-y-auto">
          <DialogHeader><DialogTitle>{duplicateFromInvoiceId ? "Duplicar factura recibida" : invoiceId ? "Editar factura recibida" : "Nueva factura recibida"}</DialogTitle></DialogHeader>
          {sourceInvoiceId && editQuery.isLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Cargando factura recibida...</div>
          ) : sourceInvoiceId && editQuery.isError ? (
            <div className="space-y-4 py-4" role="alert">
              <p className="text-sm text-destructive">No se pudo cargar la factura de origen.</p>
              <DialogFooter><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button></DialogFooter>
            </div>
          ) : duplicateFromInvoiceId && duplicateBlockedReason ? (
            <div className="space-y-4 py-4" role="alert">
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-medium">No se puede duplicar esta factura desde el formulario</p>
                <p className="mt-1">{duplicateBlockedReason}</p>
              </div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button></DialogFooter>
            </div>
          ) : complexAggregateEditBlocked ? (
            <div className="space-y-4 py-4" role="alert">
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-medium">Edición desde este formulario no disponible</p>
                <p className="mt-1">
                  Esta factura contiene {editLineCount} {editLineCount === 1 ? "línea" : "líneas"} y {editAllocationCount} {editAllocationCount === 1 ? "imputación" : "imputaciones"}.
                  Este formulario solo permite editar una línea y una imputación. Gestiona el agregado desde el detalle para no perder información.
                </p>
              </div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button></DialogFooter>
            </div>
          ) : <form onSubmit={handleSubmit} className="space-y-4">
            {duplicateSeed && <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900" role="note">
              <p className="font-medium">Nuevo borrador basado en {duplicateSeed.sourceInvoiceNumber} (ID {duplicateSeed.sourceInvoiceId})</p>
              <p className="mt-1">Revisa los campos y escribe un número y una fecha nuevos. No se copiarán adjuntos, pagos, estado ni auditoría de la factura de origen.</p>
            </div>}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Proveedor *</Label>
                <Popover open={supplierComboOpen} onOpenChange={setSupplierComboOpen}>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" role="combobox" className="w-full justify-between font-normal">
                      <span className="truncate">{selectedSupplier ? selectedSupplier.name : suppliersQuery.isLoading ? "Cargando..." : "Selecciona proveedor"}</span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar proveedor..." />
                      <CommandList>
                        <CommandEmpty>Sin resultados.</CommandEmpty>
                        <CommandGroup>
                          {suppliers.map((supplier: any) => (
                            <CommandItem key={supplier.id} value={`${supplier.name} ${supplier.taxId ?? ""}`} onSelect={() => { setForm((p) => ({ ...p, supplierId: String(supplier.id) })); setSupplierComboOpen(false); }}>
                              {supplier.name}{supplier.taxId ? <span className="ml-1 text-xs text-muted-foreground"> · {supplier.taxId}</span> : null}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                        <CommandSeparator />
                        <CommandGroup><CommandItem onSelect={() => { setSupplierComboOpen(false); setSupplierModalOpen(true); }}><Plus className="mr-2 h-4 w-4" /> Crear nuevo proveedor</CommandItem></CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="invoiceNumber">N.º factura *</Label>
                <Input id="invoiceNumber" placeholder="Requerido" value={form.invoiceNumber} onChange={set("invoiceNumber")} required />
              </div>
            </div>

            {duplicateWarning && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                <p className="font-medium">Posible factura duplicada</p>
                <p className="mt-1 text-xs">Confirma que se trata de otra factura y deja un motivo antes de guardarla.</p>
                <Label htmlFor="overrideReason" className="mt-3 block text-xs">Motivo de justificación *</Label>
                <Textarea id="overrideReason" value={form.overrideReason} onChange={set("overrideReason")} placeholder="Explica por qué debe registrarse" rows={2} className="mt-1" />
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Asignar gasto a *</Label>
                <Select value={form.allocationTarget} onValueChange={(value: "installation" | "warehouse") => setForm((p) => ({ ...p, allocationTarget: value, installationId: value === "warehouse" ? "" : p.installationId }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ALLOCATION_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.allocationTarget === "installation" && (
                <div className="space-y-1.5">
                  <Label>Obra / expediente *</Label>
                  <Popover open={installationComboOpen} onOpenChange={setInstallationComboOpen}>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" role="combobox" className="w-full justify-between font-normal text-left truncate">
                        <span className="truncate">
                          {selectedInstallation
                            ? formatInstallationReference(selectedInstallation)
                            : installationsQuery.isLoading
                            ? "Cargando..."
                            : "Selecciona obra o expediente"}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <Command filter={(value, search) => (installationMatchesSearchQuery((installationsQuery.data ?? []).find((i: any) => String(i.id) === value) ?? {}, search) ? 1 : 0)}>
                        <CommandInput placeholder="Buscar por expediente, cliente, sede, trabajo, dirección..." />
                        <CommandList>
                          <CommandEmpty>Sin expedientes encontrados.</CommandEmpty>
                          <CommandGroup>
                            {(installationsQuery.data ?? []).map((inst: any) => (
                              <CommandItem
                                key={inst.id}
                                value={String(inst.id)}
                                onSelect={() => {
                                  setForm((p) => ({ ...p, installationId: String(inst.id) }));
                                  setInstallationComboOpen(false);
                                }}
                              >
                                <span className="text-sm truncate">
                                  {formatInstallationReference({
                                    id: inst.id,
                                    installationCode: inst.installationCode,
                                    expedienteCode: inst.installationCode,
                                    clientId: inst.clientId,
                                    clientName: inst.clientName,
                                    siteName: inst.siteName,
                                    workDescription: inst.workDescription,
                                    name: inst.name,
                                    address: inst.address,
                                  })}
                                </span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="concept">Concepto *</Label>
              <Input id="concept" placeholder="Concepto de la factura" value={form.concept} onChange={set("concept")} required />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5"><Label htmlFor="invoiceDate">Fecha factura *</Label><Input id="invoiceDate" type="date" value={form.invoiceDate} onChange={set("invoiceDate")} required /></div>
              <div className="space-y-1.5"><Label htmlFor="expectedPaymentDate">Fecha prevista de pago</Label><Input id="expectedPaymentDate" type="date" value={form.expectedPaymentDate} onChange={set("expectedPaymentDate")} /></div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label htmlFor="taxableBase">Base imponible *</Label><Input id="taxableBase" type="number" step="0.01" min={0} placeholder="0.00" value={form.taxableBase} onChange={set("taxableBase")} required /></div>
              <div className="space-y-1.5">
                <Label htmlFor="vatRate">Tipo IVA *</Label>
                <Select value={form.vatRate} onValueChange={(value) => setForm((p) => ({ ...p, vatRate: value }))}>
                  <SelectTrigger id="vatRate"><SelectValue /></SelectTrigger>
                  <SelectContent>{VAT_RATES.map((rate) => <SelectItem key={rate.value} value={rate.value}>{rate.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Cuota IVA</Label><Input readOnly tabIndex={-1} value={vatAmountCalc === null ? "" : vatAmountCalc.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} placeholder="—" className="bg-muted text-muted-foreground cursor-default" /></div>
              <div className="space-y-1.5"><Label>Total</Label><Input readOnly tabIndex={-1} value={totalCalc === null ? "" : totalCalc.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} placeholder="—" className="bg-muted text-muted-foreground cursor-default" /></div>
            </div>

            <div className="space-y-1.5">
              <Label>Categoría manual</Label>
              <Select value={form.categoryCode} onValueChange={(value) => setForm((p) => ({ ...p, categoryCode: value }))}>
                <SelectTrigger><SelectValue placeholder="Automática" /></SelectTrigger>
                <SelectContent><SelectItem value={NO_CATEGORY_VALUE}>Automatica</SelectItem>{categories.map((category: any) => <SelectItem key={category.code} value={category.code}>{category.name}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Vacía equivale a clasificación automática.</p>
            </div>

            {!duplicateFromInvoiceId && <div className="space-y-1.5">
              <Label htmlFor="attachment">Factura adjunta</Label>
              <Input id="attachment" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(event) => {
                const file = event.target.files?.[0] ?? null;
                if (!file) { setAttachment(null); return; }
                if (!(ALLOWED_DOCUMENT_MIME_TYPES as readonly string[]).includes(file.type) || file.size > MAX_DOCUMENT_SIZE_BYTES) {
                  toast.error("El archivo debe ser PDF, JPG o PNG y no superar 20 MB");
                  event.currentTarget.value = "";
                  setAttachment(null);
                  return;
                }
                setAttachment(file);
              }} />
              <p className="text-xs text-muted-foreground">PDF, JPG o PNG; maximo 20 MB.</p>
            </div>}

            <div className="space-y-1.5"><Label htmlFor="notes">Observaciones</Label><Textarea id="notes" placeholder="Observaciones opcionales..." value={form.notes} onChange={set("notes")} rows={3} /></div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending || vatAmountCalc === null || (duplicateWarning && !form.overrideReason.trim())}>{createMutation.isPending || updateMutation.isPending ? "Guardando..." : invoiceId ? "Guardar cambios" : duplicateFromInvoiceId ? "Crear borrador" : duplicateWarning ? "Confirmar creacion" : "Guardar factura"}</Button>
            </DialogFooter>
          </form>}
        </DialogContent>
      </Dialog>
      <SupplierCreateModal open={supplierModalOpen} onOpenChange={setSupplierModalOpen} onCreated={handleSupplierCreated} />
    </>
  );
}
